import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { glossary } from '../data/glossary';
import { getProduct } from '../data/products';
import { Sparkles, ArrowRight, Leaf } from 'lucide-react';

// Deterministic \"herb of the week\" based on ISO week number.
function getWeekNumber() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diff = (now - start) + ((start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000);
  const oneWeek = 1000 * 60 * 60 * 24 * 7;
  return Math.floor(diff / oneWeek);
}

const FeaturedHerb = () => {
  const week = getWeekNumber();

  // Prefer entries that have a Latin name AND at least one associated product
  const eligible = useMemo(
    () => glossary.filter(g => g.latin && g.foundIn && g.foundIn.length > 0),
    []
  );

  const herb = eligible[week % eligible.length];
  const matchedProduct = herb.foundIn.map(getProduct).find(Boolean);

  if (!herb || !matchedProduct) return null;

  return (
    <section className="bg-[#efe9db]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Story */}
          <div>
            <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">
              <Sparkles className="w-4 h-4" /> Featured herb of the week
            </div>
            <h2 className="font-serif-elegant text-5xl md:text-6xl leading-tight mb-2">{herb.name}</h2>
            {herb.latin && (
              <div className="font-script text-2xl text-[#7a8b6b] mb-6">{herb.latin}</div>
            )}
            <p className="text-[#4a4640] leading-relaxed text-lg mb-6">{herb.benefits}</p>
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="px-3 py-1 text-[10px] uppercase tracking-widest bg-[#3a4a3b] text-[#faf7f1]">{herb.category}</span>
              <span className="inline-flex items-center gap-1 text-xs text-[#6f6a5f]">
                <Leaf className="w-3.5 h-3.5" /> Sourced with intention
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to={`/products/${matchedProduct.id}`} className="btn-primary inline-flex items-center gap-2">
                Try it in {matchedProduct.title} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/archive" className="btn-outline">View archive</Link>
            </div>
          </div>

          {/* Right: Product visual */}
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#cfd8c2]/60 -z-10" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full bg-[#e9d8c4]/60 -z-10" />
            <Link to={`/products/${matchedProduct.id}`} className="block bg-[#faf7f1] p-8 md:p-12 hover:shadow-xl transition-shadow">
              <div className="aspect-square bg-[#efe9db] flex items-center justify-center p-6 mb-6 overflow-hidden">
                <img
                  src={matchedProduct.images[0]}
                  alt={matchedProduct.title}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-center">
                <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-2">Featured product</div>
                <div className="font-serif-elegant text-2xl mb-1">{matchedProduct.title}</div>
                <div className="text-[#7a3b2e] font-medium">${matchedProduct.price.toFixed(2)}</div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedHerb;
